import React, { useState, useEffect, useRef } from "react";
import logo from "../assets/mhe_logo.png";
import "../css/style.css";
import "../css/bootstrap.min.css";
import "../css/NavMenu.css";
import 'bootstrap-icons/font/bootstrap-icons.css';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight } from 'react-bootstrap-icons';
import { Html5QrcodeScanner } from 'html5-qrcode';

const NavMenu = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const bearerToken = localStorage.getItem('bearerToken');

    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [showScanner, setShowScanner] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [searchOpenMobile, setSearchOpenMobile] = useState(false);

    const isProcessingScanRef = useRef(false);
    const scannerRef = useRef(null);
    const searchBoxRef = useRef(null);

    useEffect(() => {
        getCategories();
        getProducts();
    }, []);

    // Close suggestion dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close mobile menu / search on route change
    useEffect(() => {
        setSearchOpenMobile(false);
        setShowSuggestions(false);
    }, [location.pathname]);

    const getProducts = async () => {
        try {
            const response = await axios.get('/Website/getProducts', {
                headers: { Authorization: `Bearer ${bearerToken}` },
            });
            setProducts(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            if (!bearerToken) {
                console.error('Error fetching product list: bearerToken is null.');
            } else {
                console.error('Error fetching product list:', error);
            }
        }
    };

    const getCategories = async () => {
        try {
            const response = await axios.get('/Website/GetCategories', {
                headers: { Authorization: `Bearer ${bearerToken}` },
            });
            setCategories(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const sortedProducts = () => {
        return [...products].sort((a, b) => {
            const aName = (a?.proD_NAME || '').trim();
            const bName = (b?.proD_NAME || '').trim();
            const aLower = aName.toLowerCase();
            const bLower = bName.toLowerCase();

            const aIsNx64 = aLower.includes('nx64');
            const bIsNx64 = bLower.includes('nx64');
            if (aIsNx64 !== bIsNx64) return aIsNx64 ? -1 : 1;

            const aHasNx = aLower.includes('nx') ? 0 : 1;
            const bHasNx = bLower.includes('nx') ? 0 : 1;
            if (aHasNx !== bHasNx) return aHasNx - bHasNx;

            return aName.localeCompare(bName, undefined, { numeric: true, sensitivity: 'base' });
        });
    };

    const navToPage = (page, category) => {
        navigate(page, {
            state: {
                cat: categories,
                products: sortedProducts(),
                selCat: category
            }
        });
    };

    // ── Search ──────────────────────────────────────────────
    const handleSearchChange = (value) => {
        setSearchTerm(value);
        if (!value.trim()) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }
        const lower = value.toLowerCase();
        const matches = products
            .filter(p => p.proD_NAME?.toLowerCase().includes(lower))
            .slice(0, 6);
        setSuggestions(matches);
        setShowSuggestions(true);
    };

    const submitSearch = (e) => {
        if (e) e.preventDefault();
        const term = searchTerm.trim();
        setShowSuggestions(false);
        setSearchOpenMobile(false);
        navigate('/Products', {
            state: {
                cat: categories,
                products: sortedProducts(),
                selCat: 0,
                searchTerm: term
            }
        });
    };

    const goToProduct = (product) => {
        setShowSuggestions(false);
        setSearchTerm('');
        setSearchOpenMobile(false);
        navigate(`/ProdView/${encodeURIComponent(product.proD_NAME)}`, {
            state: { prod: product }
        });
    };

    const [showDropdown, setShowDropdown] = useState(false);
    let dropdownTimeout = null;

    const openDropdown = () => {
        clearTimeout(dropdownTimeout);
        setShowDropdown(true);
    };

    const closeDropdown = () => {
        dropdownTimeout = setTimeout(() => setShowDropdown(false), 120);
    };

    // ── Scanner ─────────────────────────────────────────────
    const handleCloseScanner = () => {
        setShowScanner(false);
        isProcessingScanRef.current = false;
    };

    const handleScanSuccess = async (decodedText) => {
        if (isProcessingScanRef.current) return;
        isProcessingScanRef.current = true;

        if (scannerRef.current) {
            try { await scannerRef.current.clear(); } catch (err) { /* noop */ }
        }

        const urlPrefix = "www.mhe.co.za/srl/?z=";

        const sendScannedValue = async (value) => {
            try {
                handleCloseScanner();
                const response = await axios.post(
                    "/Website/SubmitSerial",
                    {scannedValue: value},
                    {headers: { Authorization: `Bearer ${bearerToken}`}}
                );
                const data = response.data;
                if (data.match === false) {
                    alert("Product not found.");
                    return;
                }
                if (data.longname) {
                    navigate(`/ProdView/${encodeURIComponent(data.longname)}`);
                }
            } catch (error) {
                handleCloseScanner();
                console.error("Failed to send scanned value:", error);
                if (error.response) {
                    alert(`Server Error (${error.response.status}): ${error.response.data?.message || JSON.stringify(error.response.data)}`);
                } else if (error.request) {
                    alert("No response received from server.");
                } else {
                    alert("Error setting up request: " + error.message);
                }
            }
        };

        try {
            if (decodedText.startsWith(urlPrefix)) {
                const fullUrl = "https://" + decodedText;
                const urlObj = new URL(fullUrl);
                const hexCode = urlObj.searchParams.get("z");
                if (hexCode) {
                    const serialNumberString = parseInt(hexCode, 16).toString();
                    await sendScannedValue(serialNumberString);
                    return;
                }
            }
        } catch (err) {
            alert("Failed to parse scanned QR code.");
            handleCloseScanner();
            return;
        }
        await sendScannedValue(decodedText);
    };

    const BarcodeScannerModal = ({ onClose, onScan }) => {
        useEffect(() => {
            const config = { fps: 10, qrbox: { width: 250, height: 250 } };
            scannerRef.current = new Html5QrcodeScanner("scanner", config, false);
            scannerRef.current.render(
                (decodedText) => { if (onScan) onScan(decodedText); },
                () => { /* scan error spoon */ }
            );
            return () => {
                if (scannerRef.current) {
                    scannerRef.current.clear().catch(() => { /* spoon */ });
                }
            };
        }, [onClose, onScan]);

        return (
            <div className="mhe-scanner-backdrop">
                <div className="mhe-scanner-modal">
                    <div id="scanner" style={{ width: 300, height: 300 }} />
                    <button onClick={onClose} className="btn btn-dark mt-2 w-100">Close</button>
                </div>
            </div>
        );
    };

    // ─────────────────── Render ─────────────────────────────
    return (
        <nav className="navbar navbar-expand-lg sticky-top mhe-navbar p-0">
            <div className="container-fluid mhe-navbar-inner px-3 px-lg-4">

                {/* Brand */}
                <a href="/" className="navbar-brand mhe-brand d-flex align-items-center">
                    <img src={logo} alt="MHE Electronics" className="mhe-brand-logo" />
                </a>

                {/* Mobile search toggle (only visible on small screens) */}
                <button type="button" className="btn mhe-icon-btn d-lg-none ms-auto me-2" aria-label="Toggle search" onClick={() => setSearchOpenMobile(v => !v)}>
                    <i className="bi bi-search" />
                </button>

                {/* Mobile menu toggle */}
                <button type="button" className="navbar-toggler mhe-toggler" data-bs-toggle="collapse" data-bs-target="#navbarCollapse" aria-label="Toggle navigation">
                    <i className="bi bi-list" />
                </button>

                {/* Search bar (desktop, always visible; mobile, toggled) */}
                <div className={`mhe-search-wrap order-lg-2 ${searchOpenMobile ? 'is-open' : ''}`} ref={searchBoxRef}>
                    <form onSubmit={submitSearch} className="mhe-search-form" role="search">
                        <i className="bi bi-search mhe-search-icon" />
                        <input
                            type="text"
                            className="form-control mhe-search-input"
                            placeholder="Search products..."
                            value={searchTerm}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            onFocus={() => searchTerm && setShowSuggestions(true)}
                            aria-label="Search products"
                        />
                        {searchTerm && (
                            <button type="button" className="mhe-search-clear" onClick={() => { setSearchTerm(''); setSuggestions([]); setShowSuggestions(false); }} aria-label="Clear search">
                                <i className="bi bi-x-lg" />
                            </button>
                        )}
                        <button type="submit" className="btn mhe-search-submit">
                            Search
                        </button>
                    </form>

                    {showSuggestions && suggestions.length > 0 && (
                        <ul className="mhe-search-suggestions">
                            {suggestions.map((p, idx) => (
                                <li key={p.id || `${p.proD_NAME}-${idx}`}>
                                    <button type="button" onClick={() => goToProduct(p)}>
                                        <i className="bi bi-box-seam me-2" />
                                        {p.proD_NAME}
                                    </button>
                                </li>
                            ))}
                            <li className="mhe-search-suggestions-all">
                                <button type="button" onClick={submitSearch}>
                                    View all results for "{searchTerm}" <ArrowRight />
                                </button>
                            </li>
                        </ul>
                    )}

                    {showSuggestions && suggestions.length === 0 && searchTerm.trim() && (
                        <ul className="mhe-search-suggestions">
                            <li className="mhe-search-suggestions-empty">
                                No matches for "{searchTerm}"
                            </li>
                        </ul>
                    )}
                </div>

                {/* Collapsible nav links */}
                <div className="collapse navbar-collapse order-lg-1" id="navbarCollapse">
                    <div className="navbar-nav mhe-nav-links ms-lg-3">
                        <a href="/" className="nav-item nav-link">Home</a>

                        <div className={`nav-item mhe-products-nav${showDropdown ? ' open' : ''}`} onMouseEnter={openDropdown} onMouseLeave={closeDropdown}>
                            <button type="button" className="nav-link mhe-products-trigger" aria-haspopup="true" aria-expanded={showDropdown}>
                                Products
                                <i className="bi bi-chevron-down mhe-products-chevron" />
                            </button>

                            {showDropdown && (
                                <div className="mhe-mega-dropdown">
                                    {/* Left: branded intro panel */}
                                    <div className="mhe-mega-left">
                                        <span className="mhe-mega-eyebrow">Product Range</span>
                                        <h3 className="mhe-mega-heading">Find the right solution</h3>
                                        <p className="mhe-mega-copy">
                                            Browse our full lineup of emergency warning systems, control modules, and accessories.
                                        </p>
                                        <button type="button" className="mhe-mega-cta" onClick={() => { setShowDropdown(false); navToPage('/Products', 0); }}>
                                            View all products <ArrowRight size={14} />
                                        </button>
                                    </div>

                                    {/* Right: category grid */}
                                    <div className="mhe-mega-right">
                                        <span className="mhe-mega-section-label">Browse by category</span>
                                        <div className="mhe-mega-grid">
                                            {categories.map(cat => (
                                                <button key={cat.caT_ID} type="button" className="mhe-mega-cat-card" onClick={() => { setShowDropdown(false); navToPage('/Products', cat.caT_ID); }}>
                                                    <span className="mhe-mega-cat-icon">
                                                        <i className="bi bi-box-seam" />
                                                    </span>
                                                    <span className="mhe-mega-cat-name">{cat.caT_NAME}</span>
                                                    <ArrowRight size={12} className="mhe-mega-cat-arrow" />
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <a href="/about" className="nav-item nav-link">About Us</a>
                    </div>
                </div>

                {showScanner && (
                    <BarcodeScannerModal onClose={handleCloseScanner} onScan={handleScanSuccess}/>
                )}
            </div>
        </nav>
    );
};

export default NavMenu;
